import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Building,
  Upload,
  BarChart3,
  Trophy,
  Calendar,
  Wrench,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  PieChart,
  Map,
  LogOut,
  ChevronsUpDown,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const mainItems = [
  { title: "Accueil", url: "/home", icon: LayoutDashboard },
  { title: "Gestion Analyses (AO,…)", url: "/appels-offres", icon: FileText },
  { title: "Ranking Sociétés", url: "/ranking-societes", icon: Trophy },
  { title: "Reporting", url: "/reporting", icon: PieChart },
];

const administrationItems = [
  { title: "Import CV", url: "/import-cv", icon: Upload },
  { title: "Sociétés", url: "/societes", icon: Building },
  { title: "Services", url: "/services", icon: Wrench },
  { title: "Collaborateurs & Utilisateurs", url: "/collaborateurs", icon: Users },
  { title: "Types d'AO", url: "/types-appel-offres", icon: FileText },
  { title: "Analyses IA", url: "/analyses", icon: BarChart3 },
  { title: "Planning AO", url: "/planning-ao", icon: Calendar },
  { title: "Planning Gantt", url: "/planning-ao?view=gantt", icon: BarChart3 },
  { title: "Architecture", url: "/architecture", icon: Map },
  { title: "Paramètres", url: "/parametres", icon: Settings },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentPath = location.pathname;
  const { user, userRole, logout } = useAuth();

  const handleSignOut = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !"
      });
      navigate('/auth');
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    }
  };

  const isAdministrationActive = administrationItems.some((item) =>
    item.url.includes("?")
      ? item.url === `${currentPath}${location.search}`
      : currentPath === item.url || currentPath.startsWith("/types-appel-offres")
  );

  const [isAdminOpen, setIsAdminOpen] = useState(isAdministrationActive);

  const isActive = (path: string) => {
    if (path === "/planning-ao?view=gantt") {
      return currentPath === "/planning-ao" && location.search === "?view=gantt";
    }
    if (path === "/planning-ao") {
      return currentPath === "/planning-ao" && location.search !== "?view=gantt";
    }
    return currentPath === path;
  };

  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      "rounded-md border px-2 py-1.5 transition-colors",
      isActive
        ? "border-sidebar-border bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-sm"
        : "border-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    );

  const isCollapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon" className="border-sidebar-border bg-sidebar">
      <SidebarHeader className="pb-1 pt-3">
        <div className={cn("px-1", isCollapsed ? "flex justify-center" : "")}>
          <BrandLogo
            variant={isCollapsed ? "icon" : "full"}
            size="sm"
            subtitle={isCollapsed ? undefined : "EPFL"}
          />
        </div>
      </SidebarHeader>
      <SidebarSeparator />

      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupLabel
            className={`text-sidebar-foreground font-medium ${isCollapsed ? "sr-only" : ""}`}
          >
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/home"}
                      className={() => getNavCls({ isActive: isActive(item.url) })}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {userRole === "admin" && (
          <SidebarGroup>
            <Collapsible open={isAdminOpen} onOpenChange={setIsAdminOpen}>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-sidebar-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? "sr-only" : ""}`}
                >
                  Administration
                  {!isCollapsed && (
                    isAdminOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )
                  )}
                </CollapsibleTrigger>
              </SidebarGroupLabel>

              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {administrationItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild tooltip={item.title}>
                          <NavLink to={item.url} className={() => getNavCls({ isActive: isActive(item.url) })}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary font-medium uppercase">
                      {user?.email?.substring(0, 2) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.email?.split('@')[0] || "Utilisateur"}
                    </span>
                    <span className="truncate text-xs text-muted-foreground capitalize">
                      {userRole}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={isCollapsed ? "right" : "bottom"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary font-medium uppercase">
                        {user?.email?.substring(0, 2) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.email || "Utilisateur"}
                      </span>
                      <span className="truncate text-xs text-muted-foreground capitalize">
                        {userRole}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-600 focus:bg-red-100 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Déconnexion</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
